import { useState, useEffect } from "react";
import { Globe, Languages } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { LocalizationFilters } from "@/components/localization/LocalizationFilters";
import { TranslationTable } from "@/components/localization/TranslationTable";
import { ImportExportControls } from "@/components/localization/ImportExportControls";
import {
  useClientTranslations,
  useUpdateClientTranslation,
  useImportClientTranslations,
  useExportClientTranslations,
  useResetClientTranslations,
} from "@/hooks/useClientTranslations";

interface OrganizationLocalizationPageProps {
  organizationId: string;
}

export function OrganizationLocalizationPage({ organizationId }: OrganizationLocalizationPageProps) {
  const [selectedLocale, setSelectedLocale] = useState("en");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Queries and mutations
  const { data: translations = [], isLoading } = useClientTranslations(
    organizationId,
    selectedLocale,
    debouncedSearch
  );
  
  const updateTranslation = useUpdateClientTranslation(organizationId);
  const importTranslations = useImportClientTranslations(organizationId);
  const exportTranslations = useExportClientTranslations(organizationId);
  const resetTranslations = useResetClientTranslations(organizationId);

  const handleUpdateTranslation = (key: string, value: string, locale: string) => {
    updateTranslation.mutate({ key, locale, value });
  };

  const handleImport = (file: File) => {
    importTranslations.mutate(file);
  };

  const handleExport = () => {
    exportTranslations(selectedLocale);
  };

  const handleReset = () => {
    resetTranslations.mutate(selectedLocale);
  };

  // Calculate statistics
  const stats = {
    total: translations.length,
    customized: translations.filter(t => t.isModified).length,
    defaults: translations.filter(t => !t.isModified).length,
  };

  const availableLocales = ['en', 'ar'];

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Globe className="h-6 w-6" />
            Localization Settings
          </h2>
          <p className="text-muted-foreground">
            Manage client-specific translations and language preferences
          </p>
        </div>
        <ImportExportControls
          onImport={handleImport}
          onExport={handleExport}
          onReset={handleReset}
          isImporting={importTranslations.isPending}
          locale={selectedLocale}
        />
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Keys</CardTitle>
            <Languages className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">
                  Translation keys available
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Customized</CardTitle>
            <Badge variant="default" className="bg-green-600">Custom</Badge>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.customized}</div>
                <p className="text-xs text-muted-foreground">
                  Client-specific translations
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Using Defaults</CardTitle>
            <Badge variant="secondary">Default</Badge>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats.defaults}</div>
                <p className="text-xs text-muted-foreground">
                  Inheriting from system
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <LocalizationFilters
        selectedLocale={selectedLocale}
        onLocaleChange={setSelectedLocale}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        availableLocales={availableLocales}
      />

      {/* Translation Table */}
      <TranslationTable
        translations={translations}
        onEdit={handleUpdateTranslation}
        isLoading={isLoading}
      />
    </div>
  );
}